/**
 * =============================================================================
 * RFI REQUEST CARD COMPONENT
 * =============================================================================
 *
 * Renders one `AdminRfiRequest`: the rollup-status badge, the staff prose
 * (`message`), who opened it (a generic "Staff" label — never the raw user id),
 * timestamps, its ordinal-sorted items (via `RfiItemRow`), and the "Add
 * evidence" control that opens the link picker for THIS RFI.
 *
 * The internal RFI anchor `replyId` is an implementation artifact and is NEVER
 * displayed (§2b). Terminal (SUPERSEDED/CANCELLED) RFIs render read-only.
 *
 * =============================================================================
 */

import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import type { AdminReplyListItem, AdminRfiRequest, CreateRfiResponseLinkInput } from '@find-my-part/types';
import { formatDate } from '../../../utils/formatters';
import type { SettableReviewStatus } from '../../../hooks/useRfi';
import type { UseRfiCandidateRepliesResult } from '../../../hooks/useRfiCandidateReplies';
import { RfiItemRow } from './RfiItemRow';
import { RfiLinkPicker } from './RfiLinkPicker';
import { isTerminalRfiStatus, RFI_STATUS_LABELS, RFI_STATUS_VARIANTS } from './rfiLabels';
import { tokens } from '../../../tokens';
import { StatusChip } from '../../common/StatusChip';

/**
 * Props for RfiRequestCard.
 */
export interface RfiRequestCardProps {
  /** The RFI request to render. */
  rfi: AdminRfiRequest;
  /**
   * Auth token — threaded into the link picker for the adjacent authenticated
   * image previews of RFI-linkable evidence (ADR-022 §14 D5).
   */
  token: string;
  /** Hook pending-key set. */
  pendingActions: Set<string>;
  /** Set an item's review status. */
  reviewItem: (rfiId: string, rfiItemId: string, reviewStatus: SettableReviewStatus) => void;
  /** Unlink a response. */
  unlinkResponse: (rfiId: string, rfiResponseId: string) => void;
  /** Link evidence. Resolves true on success. */
  linkResponses: (rfiId: string, input: CreateRfiResponseLinkInput) => Promise<boolean>;
  /**
   * Cancel (withdraw) this RFI — a terminal transition. Resolves true on success.
   * The panel wraps this to also close an open picker for the cancelled RFI.
   */
  cancelRfi: (rfiId: string) => Promise<boolean>;
  /** Candidate-reply loader (shared by the open picker). */
  candidates: UseRfiCandidateRepliesResult;
  /** Candidate replies by id (linked-response excerpt fallback). */
  candidateRepliesById: Map<string, AdminReplyListItem>;
  /** Whether THIS RFI's link picker is open. */
  isPickerOpen: boolean;
  /** Open this RFI's link picker. */
  onOpenPicker: (rfiId: string) => void;
  /** Close the link picker. */
  onClosePicker: () => void;
  /** Optional test ID prefix. */
  testId?: string;
}

/**
 * One RFI request card.
 */
export function RfiRequestCard({
  rfi,
  token,
  pendingActions,
  reviewItem,
  unlinkResponse,
  linkResponses,
  cancelRfi,
  candidates,
  candidateRepliesById,
  isPickerOpen,
  onOpenPicker,
  onClosePicker,
  testId,
}: RfiRequestCardProps): React.ReactElement {
  const isReadOnly = isTerminalRfiStatus(rfi.status);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  // Disable the cancel control while ANY op is pending on THIS RFI (across the
  // heterogeneous pending keys) — never act on a half-mutated RFI.
  const pendingOnThisRfi =
    pendingActions.has(`cancel:${rfi.rfiId}`) ||
    pendingActions.has(`link:${rfi.rfiId}`) ||
    rfi.items.some(
      (i) =>
        pendingActions.has(`review:${i.rfiItemId}`) ||
        i.responses.some((r) => pendingActions.has(`unlink:${r.rfiResponseId}`))
    );
  const cancelPending = pendingActions.has(`cancel:${rfi.rfiId}`);

  // Render items sorted by ordinal — never rely on incidental array order.
  const sortedItems = useMemo(
    () => [...rfi.items].sort((a, b) => a.ordinal - b.ordinal),
    [rfi.items]
  );

  // (replyId, rfiItemId) pairs already linked anywhere on this RFI — disables
  // duplicate link targets in the picker client-side (the backend 409s anyway).
  const alreadyLinkedPairs = useMemo(() => {
    const pairs = new Set<string>();
    for (const item of rfi.items) {
      for (const response of item.responses) {
        pairs.add(`${response.replyId}::${item.rfiItemId}`);
      }
    }
    return pairs;
  }, [rfi.items]);

  return (
    <Box
      role="listitem"
      padding="small"
      marginBottom="small"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.lg }}
      data-testid={testId}
    >
      <Flex alignItems="center" justifyContent="space-between" flexGap="0.5rem">
        <FlexItem>
          <Flex flexGap="0.5rem" alignItems="center">
            <FlexItem>
              <StatusChip label={RFI_STATUS_LABELS[rfi.status]} variant={RFI_STATUS_VARIANTS[rfi.status]} />
            </FlexItem>
            <FlexItem>
              <Text color="secondary">
                Opened by Staff · {formatDate(rfi.createdAt)}
              </Text>
            </FlexItem>
          </Flex>
        </FlexItem>
        {rfi.status === 'ANSWERED' && rfi.completedAt && (
          <FlexItem>
            <Text color="secondary">
              Answered {formatDate(rfi.completedAt)}
            </Text>
          </FlexItem>
        )}
      </Flex>

      <Box marginTop="xSmall">
        <Text>{rfi.message}</Text>
      </Box>

      {isReadOnly && (
        <Text
          color="secondary"
          data-testid={testId ? `${testId}-readonly-note` : undefined}
        >
          This RFI is {RFI_STATUS_LABELS[rfi.status].toLowerCase()} and read-only.
        </Text>
      )}

      <Box
        role="list"
        aria-label="RFI items"
        marginTop="small"
        data-testid={testId ? `${testId}-items` : undefined}
      >
        {sortedItems.map((item) => (
          <RfiItemRow
            key={item.rfiItemId}
            item={item}
            isReadOnly={isReadOnly}
            pendingActions={pendingActions}
            // Freeze review/unlink while a terminal cancel is in flight on this RFI.
            disabled={cancelPending}
            onReview={(reviewStatus) => reviewItem(rfi.rfiId, item.rfiItemId, reviewStatus)}
            onUnlink={(rfiResponseId) => unlinkResponse(rfi.rfiId, rfiResponseId)}
            candidateRepliesById={candidateRepliesById}
            testId={testId ? `${testId}-item-${item.rfiItemId}` : undefined}
          />
        ))}
      </Box>

      {!isReadOnly && (
        <Box marginTop="xSmall">
          {isPickerOpen ? (
            <RfiLinkPicker
              rfiId={rfi.rfiId}
              token={token}
              items={sortedItems}
              candidates={candidates}
              alreadyLinkedPairs={alreadyLinkedPairs}
              // Block linking while a terminal cancel is in flight on this RFI.
              isSubmitting={pendingActions.has(`link:${rfi.rfiId}`) || cancelPending}
              onLink={(input) => linkResponses(rfi.rfiId, input)}
              onClose={onClosePicker}
              testId={testId ? `${testId}-picker` : undefined}
            />
          ) : (
            <Button
              variant="secondary"
              onClick={() => onOpenPicker(rfi.rfiId)}
              disabled={cancelPending}
              data-testid={testId ? `${testId}-add-evidence` : undefined}
            >
              Add evidence
            </Button>
          )}
        </Box>
      )}

      {/* Cancel / withdraw (PR H). Non-terminal RFIs only; confirm-gated and
          disabled while ANY op is pending on this RFI. No optimistic removal —
          on success the server returns the CANCELLED RFI and this card re-renders
          read-only. */}
      {!isReadOnly && (
        <Box marginTop="xSmall">
          {confirmingCancel ? (
            <Box
              padding="xSmall"
              style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.sm }}
              data-testid={testId ? `${testId}-cancel-confirm` : undefined}
            >
              <Text>
                Withdraw this RFI? This can&apos;t be undone.
              </Text>
              <Flex flexGap="0.25rem" marginTop="xSmall">
                <FlexItem>
                  <Button
                    variant="primary"
                    actionType="destructive"
                    disabled={pendingOnThisRfi}
                    isLoading={cancelPending}
                    onClick={async () => {
                      const ok = await cancelRfi(rfi.rfiId);
                      // On success the RFI returns CANCELLED and this card
                      // re-renders read-only (this confirm UI unmounts). On
                      // failure (e.g. a concurrent re-cancel 409) drop back to the
                      // idle control so the operator can see the error and retry.
                      if (!ok) setConfirmingCancel(false);
                    }}
                    data-testid={testId ? `${testId}-cancel-confirm-yes` : undefined}
                  >
                    Withdraw RFI
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="subtle"
                    disabled={cancelPending}
                    onClick={() => setConfirmingCancel(false)}
                    data-testid={testId ? `${testId}-cancel-confirm-no` : undefined}
                  >
                    Keep RFI
                  </Button>
                </FlexItem>
              </Flex>
            </Box>
          ) : (
            <Button
              variant="subtle"
              disabled={pendingOnThisRfi}
              onClick={() => setConfirmingCancel(true)}
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              Cancel RFI
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
